import { useMemo, type FC } from 'react';
import MilestoneCard, { type Milestone } from './MilestoneCard';

interface RoadmapTimelineViewProps {
  milestones: Milestone[];
  onEdit: (m: Milestone) => void;
  onDelete: (id: string) => void;
}

/**
 * Groups milestones by month and renders them on a vertical timeline axis.
 * Milestones without dueDate are grouped under "Non planifie".
 */
const RoadmapTimelineView: FC<RoadmapTimelineViewProps> = ({ milestones, onEdit, onDelete }) => {
  const grouped = useMemo(() => {
    const map = new Map<string, Milestone[]>();

    // Sort by dueDate ascending, null last
    const sorted = [...milestones].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return a.sortOrder - b.sortOrder;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    });

    for (const m of sorted) {
      const key = m.dueDate
        ? new Date(m.dueDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        : 'Non planifie';
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    }

    return Array.from(map.entries());
  }, [milestones]);

  if (milestones.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 dark:text-slate-500">
        Aucun jalon. Ajoutez-en un pour commencer.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical axis */}
      <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />

      <div className="space-y-8 pl-10">
        {grouped.map(([monthLabel, items]) => (
          <div key={monthLabel}>
            {/* Month label */}
            <div className="relative flex items-center gap-3 mb-4">
              <div className="absolute -left-[26px] w-3 h-3 rounded-full bg-blue-500 border-2 border-white dark:border-slate-800 z-10" />
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize">
                {monthLabel}
              </h3>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {items.length} jalon{items.length > 1 ? 's' : ''}
              </span>
            </div>

            {/* Cards in this month */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((m) => (
                <MilestoneCard key={m.id} milestone={m} onEdit={onEdit} onDelete={onDelete} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoadmapTimelineView;
